import React from "react";
import { motion } from "framer-motion";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import {
    Check,
    X,
    Sparkles,
    Brain,
    FileText,
    Heart,
    Globe,
} from "lucide-react";

const comparisons = [
    // AI-exclusive capabilities (only Doctorite)
    {
        feature: "Real-Time AI Session Guidance",
        doctorite: true,
        others: false,
    },
    {
        feature: "Emotion Detection & Sentiment Analysis",
        doctorite: true,
        others: false,
    },
    {
        feature: "Auto-Generated SOAP/DAP Notes",
        doctorite: true,
        others: "Basic" as const,
    },
    { feature: "AI-Powered Treatment Plans", doctorite: true, others: false },
    {
        feature: "Automated ICD Code Extraction",
        doctorite: true,
        others: false,
    },
    {
        feature: "Built-in Telehealth (Native)",
        doctorite: true,
        others: "Add-on" as const,
    },
    { feature: "Multilingual Transcription", doctorite: true, others: false },
    {
        feature: "Patient Emotion Tracking Over Time",
        doctorite: true,
        others: false,
    },
    { feature: "Zero Click Appointment Scheduling", doctorite: true, others: false },

    // Platform features — Doctorite is better, others have partial or add-on
    { feature: "Claim Submission", doctorite: true, others: "Add-on" as const },
    { feature: "Real-Time Revenue Tracking", doctorite: true, others: "Basic" as const },
    { feature: "Custom Build Assessments", doctorite: true, others: "Basic" as const },
    { feature: "Professional Website", doctorite: true, others: "Add-on" as const },

    // Standard features both platforms have
    { feature: "Client Portal", doctorite: true, others: true },
    { feature: "HIPAA Compliance", doctorite: true, others: true },
    { feature: "Provider & Patient Apps", doctorite: true, others: true },
    { feature: "Group Therapy", doctorite: true, others: true },
    { feature: "Secure Messaging", doctorite: true, others: true },
    { feature: "Autopay", doctorite: true, others: true },
];

const aiCapabilities = [
    {
        icon: Brain,
        title: "Thinks During Sessions",
        description:
            "Real Time AI Guidance Supports Clinical Sessions Using Patient History, Prognosis, Risk Factors, and Evidence Based Therapies.",
    },
    {
        icon: Heart,
        title: "Reads Between the Lines",
        description:
            "Emotion detection analyzes voice tone and language patterns to identify stress, anxiety, and crisis indicators.",
    },
    {
        icon: FileText,
        title: "Documents Automatically",
        description:
            "SOAP notes, MSE, progress summaries, and treatment plans generate themselves from live session data.",
    },
    {
        icon: Globe,
        title: "Speaks Every Language",
        description:
            "Multilingual transcription and translation ensures inclusive care for diverse patient populations.",
    },
];

export default function Differentiation() {
    const { ref, inView } = useScrollAnimation();

    return (
        <section
            id="differentiation"
            className="section-padding bg-gradient-to-b from-white via-brand-50/30 to-white"
        >
            <div className="container-max" ref={ref}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                    className="text-center max-w-3xl mx-auto mb-16"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-sm font-medium text-brand-700 mb-4">
                        <Sparkles className="w-4 h-4" />
                        Beyond Traditional Practice Management
                    </div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
                        Most Behavioral Health Platforms Manage Tasks.{" "}
                        <span className="gradient-text">
                            Doctorite Delivers Real Time Clinical & Revenue
                            Intelligence.
                        </span>
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed">
                        Traditional EHRs Digitize Workflows. Doctorite Gives
                        Behavioral Health Teams Hours Back Every Day With Real
                        Time AI Automation.
                    </p>
                </motion.div>

                {/* AI Capabilities Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
                    {aiCapabilities.map((cap, index) => (
                        <motion.div
                            key={cap.title}
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="group relative bg-white rounded-2xl p-6 shadow-lg shadow-brand-900/5 border border-gray-100 hover:border-brand-200 hover:shadow-xl hover:shadow-brand-900/10 transition-all duration-300"
                        >
                            <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                                <cap.icon className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">
                                {cap.title}
                            </h3>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                {cap.description}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {/* Comparison Table */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="bg-white rounded-2xl shadow-xl shadow-brand-900/5 border border-gray-100 overflow-hidden"
                >
                    <div className="bg-gradient-to-r from-brand-600 to-brand-700 px-6 py-5">
                        <h3 className="text-xl font-bold text-white">
                            Feature Comparison
                        </h3>
                        <p className="text-brand-200 text-sm mt-1">
                            See what sets Doctorite apart from traditional
                            platforms
                        </p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full" role="table">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th
                                        className="text-left px-6 py-4 text-sm font-semibold text-gray-500 uppercase tracking-wider"
                                        scope="col"
                                    >
                                        Capability
                                    </th>
                                    <th
                                        className="text-center px-6 py-4 text-sm font-semibold text-brand-600 uppercase tracking-wider"
                                        scope="col"
                                    >
                                        Doctorite AI
                                    </th>
                                    <th
                                        className="text-center px-6 py-4 text-sm font-semibold text-gray-400 uppercase tracking-wider"
                                        scope="col"
                                    >
                                        Traditional Platforms
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {comparisons.map((row, index) => (
                                    <tr
                                        key={row.feature}
                                        className={`border-b border-gray-50 ${index < 9 ? "bg-brand-50/30" : ""} hover:bg-brand-50/50 transition-colors`}
                                    >
                                        <td className="px-6 py-3.5 text-sm text-gray-700 font-medium">
                                            {index < 9 && (
                                                <span className="inline-block w-1.5 h-1.5 bg-accent-500 rounded-full mr-2" />
                                            )}
                                            {row.feature}
                                        </td>
                                        <td className="px-6 py-3.5 text-center">
                                            {row.doctorite === true ? (
                                                <span className="inline-flex items-center justify-center w-7 h-7 bg-accent-100 rounded-full">
                                                    <Check className="w-4 h-4 text-accent-600" />
                                                </span>
                                            ) : (
                                                <span className="text-sm text-gray-500">
                                                    {String(row.doctorite)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-3.5 text-center">
                                            {row.others === true ? (
                                                <span className="inline-flex items-center justify-center w-7 h-7 bg-gray-100 rounded-full">
                                                    <Check className="w-4 h-4 text-gray-400" />
                                                </span>
                                            ) : row.others === false ? (
                                                <span className="inline-flex items-center justify-center w-7 h-7 bg-red-50 rounded-full">
                                                    <X className="w-4 h-4 text-red-300" />
                                                </span>
                                            ) : (
                                                <span className="inline-block px-2.5 py-0.5 bg-yellow-50 text-yellow-700 text-xs font-medium rounded-full">
                                                    {String(row.others)}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                        <p className="text-xs text-gray-500 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 bg-accent-500 rounded-full" />
                            AI-exclusive features not available on traditional
                            behavioral health platforms
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
